import { z } from 'zod';
import { clientFamilySchema } from './client.js';
import { lotPipelineSchema } from './lot-pipeline.js';
/**
 * Ce que le tableau de bord montre du répertoire — le tableau « Suivi par type de client » du
 * prototype (`ghp-bo/screens.js`, `catAgg`) : par **famille d'affichage**, combien de clients,
 * combien de demandes, combien sont validées, et **où en sont les autres** (R-B1).
 *
 * ⚠️ **Par famille et non par catégorie**, comme les compteurs d'onglets du répertoire : c'est le
 * regroupement des écrans, et deux partitions pour la même chose finiraient par diverger.
 *
 * ⚠️ **Le donneur d'ordre d'une demande se lit par ses deux chemins**, M32 devant M14 —
 * `Inquiry.client_id`, sinon le client du lot de son lieu —, comme la file d'export et le tableau
 * des demandes. Par le seul lot, un particulier (qui n'a pas de lot) n'aurait jamais aucune
 * demande, et la ligne « Particulier » du prototype resterait à zéro.
 *
 * ⚠️ Sorti de `client.ts` pour une raison de graphe : `lot-pipeline.ts` lit `inquiry-list.ts`, qui
 * lit `client.ts` — l'y déclarer aurait bouclé les trois modules.
 *
 * Les trois familles sont exigées : `z.record` sur l'énumération réclame toutes ses clés, si bien
 * qu'une famille absente de la réponse ne peut pas se lire « aucun client » à l'écran.
 */
export const clientFamilyBreakdownSchema = z.record(clientFamilySchema, z.object({
    /**
     * Les clients **actifs** de la famille — un client archivé ne compte plus (R-B11). Sous un
     * marché : ceux qui y ont au moins une demande, le marché se lisant sur la commune (M31) et
     * jamais sur le client.
     */
    clients: z.number().int().min(0),
    /** Toutes leurs demandes, abandonnées comprises — archivés compris : le travail fait reste. */
    inquiries: z.number().int().min(0),
    /** Celles qui sont `validated` — ni archivées, ni abandonnées. */
    validated: z.number().int().min(0),
    /**
     * La colonne « À planifier » : la vue `to_plan` du tableau des demandes, comptée en base (D24)
     * — un contrôle attendu, sans créneau, d'un type que le bureau planifie. Le systématique et la
     * SYAM s'en écartent (#695) : l'agent les planifie sur place.
     *
     * ⚠️ **Pas déduite du funnel**, qui ne connaît pas le type : `received + to_plan` les y
     * compterait. Ils restent dans « Contrôles en cours ».
     */
    toPlan: z.number().int().min(0),
    /**
     * Le funnel de la famille, **même partition que la fiche client** (`LOT_PIPELINE_PARTITION`),
     * compté en base (D24). Les autres colonnes du prototype s'en déduisent : « Contrôles en
     * cours » = `received + to_plan + scheduled + processing`, « RDV pris » = `scheduled`, « En
     * traitement » = `processing`.
     */
    pipeline: lotPipelineSchema,
}));
//# sourceMappingURL=client-family-breakdown.js.map